/**
 * Step1VehicleInfo — Vehicle & Service Info section in the JC modal.
 *
 * Owns the rego / year / make / model / odometer / service-date /
 * next-service-date inputs. The parent (JobCardDetailModal) holds the
 * state and persists everything in one PUT /mechanic-update on save.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Car } from 'lucide-react';
import { formatRego } from '@/lib/formatters';

export default function Step1VehicleInfo({
  canEdit,
  vehRego, setVehRego,
  vehYear, setVehYear,
  vehMake, setVehMake,
  vehModel, setVehModel,
  odometer, setOdometer,
  serviceDate, setServiceDate,
  nextSvcDate, setNextSvcDate,
}) {
  return (
    <section
      className="border border-blue-200 rounded-md bg-blue-50/30"
      data-testid="jc-step-1-vehicle-info"
    >
      <header className="flex items-center gap-2 px-3 py-2 border-b border-blue-200 bg-blue-100/50 rounded-t-md">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">1</span>
        <Car className="h-4 w-4 text-blue-700" />
        <h4 className="text-sm font-semibold text-blue-900">Vehicle &amp; Service Info</h4>
        <span className="text-[11px] text-blue-700/80 ml-auto">Mechanic confirms rego / year / make / model / odo</span>
      </header>
      <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <div>
          <Label htmlFor="jc-rego" className="text-xs">Rego</Label>
          <Input id="jc-rego" value={vehRego} onChange={(e) => setVehRego(formatRego(e.target.value))} disabled={!canEdit} className="mt-1 h-11 font-mono text-base touch-manipulation" data-testid="jc-veh-rego" />
        </div>
        <div>
          <Label htmlFor="jc-year" className="text-xs">Year</Label>
          <Input id="jc-year" type="number" inputMode="numeric" min="1900" max="2099" value={vehYear} onChange={(e) => setVehYear(e.target.value)} disabled={!canEdit} className="mt-1 h-11 text-base touch-manipulation" data-testid="jc-veh-year" />
        </div>
        <div>
          <Label htmlFor="jc-make" className="text-xs">Make</Label>
          <Input id="jc-make" value={vehMake} onChange={(e) => setVehMake(e.target.value)} disabled={!canEdit} className="mt-1 h-11 text-base touch-manipulation" data-testid="jc-veh-make" />
        </div>
        <div>
          <Label htmlFor="jc-model" className="text-xs">Model</Label>
          <Input id="jc-model" value={vehModel} onChange={(e) => setVehModel(e.target.value)} disabled={!canEdit} className="mt-1 h-11 text-base touch-manipulation" data-testid="jc-veh-model" />
        </div>
        <div>
          <Label htmlFor="jc-odo" className="text-xs flex items-center gap-1">
            <span>Odometer (km)</span>
            <span className="text-red-500" title="Required to submit for SM review">*</span>
          </Label>
          <Input id="jc-odo" type="number" inputMode="numeric" min="0" value={odometer} onChange={(e) => setOdometer(e.target.value)} disabled={!canEdit} className="mt-1 h-11 text-base touch-manipulation" data-testid="jc-odometer" />
        </div>
        <div>
          <Label htmlFor="jc-svc-date" className="text-xs flex items-center justify-between">
            <span>Service Date</span>
            {canEdit && !serviceDate && (
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Melbourne' });
                  setServiceDate(today);
                }}
                className="text-[11px] text-blue-600 hover:underline min-h-9 px-1 touch-manipulation"
                data-testid="jc-svc-today"
              >
                ↘ Mark done today
              </button>
            )}
          </Label>
          <Input id="jc-svc-date" type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} disabled={!canEdit} className="mt-1 h-11 text-base touch-manipulation" data-testid="jc-service-date" />
          <p className="text-[10px] text-slate-500 mt-0.5">Only set if a Service task was performed.</p>
        </div>
        <div>
          <Label htmlFor="jc-next-svc" className="text-xs flex items-center gap-1">
            <span>Next Service Due</span>
            <span className="text-red-500" title="Required to submit for SM review">*</span>
          </Label>
          <Input id="jc-next-svc" type="date" value={nextSvcDate} onChange={(e) => setNextSvcDate(e.target.value)} disabled={!canEdit} className="mt-1 h-11 text-base touch-manipulation" data-testid="jc-next-svc-date" />
        </div>
      </div>
    </section>
  );
}
